import {
  AccessLevel,
  AccountData,
  Event,
  ThemeType,
} from "@markit/common.types";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ProfilePic from "../../../ProfilePic";
import { useTheme } from "../../../../hooks/useTheme";
import { useNavigate } from "react-router-dom";
import { onProfilePageNavigatePath } from "../../../../utils/navigationUtils";
import { useDispatch } from "react-redux";
import { DataLoaders } from "../../../../redux/slices/dataSlice";
import { Colors } from "../../../../utils/colors";
import { getShareDomain, onShareLink } from "../../../../utils/shareLink";
import {
  GetDateWithTime,
  isProductNonPostType,
  getCanonicalMediaUrls,
} from "@markit/common.utils";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import RectangleButton from "../../../Buttons/RectangleButton";
import { downloadMediaFromFirestore } from "../../../../utils/mediaUtils";
import { Icon } from "@iconify/react";
import ReadOnlyRichTextEditor from "../../../TextFields/ReadOnlyRichTextEditor";
import { isDesktop } from "react-device-detect";
import { generateTextFromJSON } from "../../../../utils/stringUtils";

type FullProductHeaderSectionProps = {
  product: Event;
  creatorData: AccountData;
  hardTheme: ThemeType;
  contentHidden?: boolean;
  renderCheckoutComp?: ReactNode;
  aboveBodyComp?: ReactNode;
};

// The product header section with title, description, and main actions related to creator
const FullProductHeaderSection = (props: FullProductHeaderSectionProps) => {
  const {
    product,
    creatorData,
    hardTheme,
    contentHidden,
    renderCheckoutComp,
    aboveBodyComp,
  } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [onSubscribeHover, setOnSubscribeHover] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme(hardTheme);

  const styles = {
    buttonContainer: {
      padding: "7px 12px",
      borderRadius: 100,
      border: `0.5px solid ${theme.SubDividerColor.borderColor}`,
      gap: 4,
    },
  };

  const navigateProfileOnPress = useCallback(
    (isSubscribe: boolean) => {
      dispatch(DataLoaders.user(creatorData.uid));
      onProfilePageNavigatePath(
        navigate,
        creatorData.username,
        isSubscribe ? "subscribe" : undefined
      );
    },
    [creatorData.uid, creatorData.username, dispatch, navigate]
  );

  const productUrl = useMemo(
    () => `${getShareDomain()}/e/${product.id}`,
    [product.id]
  );

  const bodyContent = useMemo(
    () => generateTextFromJSON(product.body),
    [product.body]
  );

  const shareUrlLink = useCallback(async () => {
    await onShareLink("Share this product!", productUrl);
  }, [productUrl]);

  const downloadContent = useCallback(async () => {
    const mediaUrls = getCanonicalMediaUrls(product);
    if (mediaUrls.length > 0) {
      setIsDownloading(true);
      // Only download the first media file due to issues with downloading multiple files at once on mobile
      await downloadMediaFromFirestore(mediaUrls.slice(0, 1), product.title);
      setIsDownloading(false);
    }
  }, [product]);

  // Track container width changes
  useEffect(() => {
    const currentContainerRef = containerRef.current;
    if (!currentContainerRef) return;

    const updateWidth = () => {
      if (currentContainerRef) {
        setContainerWidth(currentContainerRef.offsetWidth);
      }
    };

    // Set initial width
    updateWidth();

    // Observe width changes
    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(currentContainerRef);

    return () => {
      if (currentContainerRef) {
        observer.unobserve(currentContainerRef);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="ColumnNormal" style={{ gap: 24 }}>
      <div className="ColumnNormal" style={{ gap: 10 }}>
        <span
          style={{
            fontSize: isDesktop ? 28 : 24,
            fontWeight: 500,
            ...theme.PrimaryText,
            wordBreak: "break-word",
          }}
        >
          {product.title}
        </span>
        {product.description ? (
          <span className="bodyRegular" style={theme.LabelText}>
            {product.description}
          </span>
        ) : null}
      </div>
      {(product.accessLevel === AccessLevel.CHECKOUT && renderCheckoutComp) ||
      aboveBodyComp ? (
        <div className="ColumnNormal" style={{ gap: 7 }}>
          {product.accessLevel === AccessLevel.CHECKOUT && renderCheckoutComp
            ? renderCheckoutComp
            : null}
          {aboveBodyComp ? (
            <div style={{ alignSelf: "center" }}>{aboveBodyComp}</div>
          ) : null}
        </div>
      ) : null}
      {isProductNonPostType(product.eventType) && bodyContent ? (
        <ReadOnlyRichTextEditor
          body={product.body}
          theme={theme}
          className={`post-content-editor ${isDesktop ? "" : "mobile-editor"}`}
        />
      ) : null}
      <div className="ColumnNormal" style={{ gap: 14 }}>
        <div className="AlignedRowSpaced" style={{ gap: 14 }}>
          <div
            className="AlignedRowSelect"
            style={{ gap: 10 }}
            onClick={() => navigateProfileOnPress(false)}
          >
            <div>
              <ProfilePic user={creatorData} size={20} forcePointer />
            </div>
            <span
              className="bodySubtext"
              style={{ textDecoration: "underline" }}
            >
              By {creatorData.fullName}
            </span>
          </div>
          <span className="bodySubtext" style={{ whiteSpace: "nowrap" }}>
            {GetDateWithTime(product.createdAt, true)}
          </span>
        </div>
        <HorizontalDivider altMargin={0} eventTheme={hardTheme} altWidth={1} />
        <div className="AlignedRowSpaced" style={{ gap: 14 }}>
          <div
            className="RowNormal"
            style={{
              ...styles.buttonContainer,
              cursor: "pointer",
              backgroundColor: onSubscribeHover
                ? theme.ButtonBG.backgroundColor
                : Colors.TRANSPARENT,
            }}
            onClick={() => navigateProfileOnPress(true)}
            onMouseEnter={() => setOnSubscribeHover(true)}
            onMouseLeave={() => setOnSubscribeHover(false)}
          >
            <span
              className="smallBodySubtext"
              style={{
                color: onSubscribeHover
                  ? theme.ButtonText.color
                  : theme.PrimaryText.color,
              }}
            >
              Subscribe
            </span>
          </div>
          <div className="AlignedRow" style={{ gap: 10 }}>
            {containerWidth < 280 ? (
              <Icon
                icon="ion:share"
                height={18}
                style={{ cursor: "pointer", color: theme.PrimaryText.color }}
                onClick={shareUrlLink}
              />
            ) : (
              <RectangleButton
                buttonLabel={<span className="bodySmallRegular">Share</span>}
                onPress={shareUrlLink}
                altColor={theme.NewPrimaryBG.backgroundColor}
                altTextColor={theme.PrimaryText.color}
                containerStyles={styles.buttonContainer}
              />
            )}
            {!contentHidden && product.mediaDownloadable ? (
              <RectangleButton
                buttonLabel={
                  <span className="bodySmallRegular">
                    {isDownloading ? "Downloading..." : "Download"}
                  </span>
                }
                iconRight={
                  <Icon
                    icon="ion:download"
                    height={14}
                    style={{ color: theme.PrimaryText.color, marginTop: -2 }}
                  />
                }
                onPress={downloadContent}
                altColor={theme.NewPrimaryBG.backgroundColor}
                altTextColor={theme.PrimaryText.color}
                containerStyles={styles.buttonContainer}
                loading={isDownloading}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullProductHeaderSection;
